import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { cn, Dialog, DialogContent, Button } from "@tabipla/ui";
import { ChevronRight, Lightbulb } from "lucide-react";
import { onboardingAtom } from "@/application/atoms/onboarding-atoms";
import { Illustration } from "@/presentation/components/Illustration";

const STEPS = [
  { illustration: "travel-plans", translationKey: "step1" },
  { illustration: "chat-with-ai", translationKey: "step2" },
  { illustration: "share-link", translationKey: "step3" },
  { illustration: "celebration", translationKey: "complete" },
] as const;

export function OnboardingModal() {
  const { t } = useTranslation();
  const [onboarding, setOnboarding] = useAtom(onboardingAtom);
  const [step, setStep] = useState(0);

  const isOpen = !onboarding.welcomeModalShown;
  const isLastStep = step === STEPS.length - 1;
  const currentStep = STEPS[step];

  const handleClose = () => {
    setOnboarding({
      welcomeModalShown: true,
      completedAt: new Date().toISOString(),
    });
  };

  const handleNext = () => {
    if (isLastStep) {
      handleClose();
    } else {
      setStep((prev) => prev + 1);
    }
  };

  const handleSkip = () => {
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="max-w-sm overflow-hidden p-0 sm:max-w-md"
        hideCloseButton
      >
        {/* Step indicator */}
        {!isLastStep && (
          <div className="absolute left-4 top-4 text-sm text-muted-foreground">
            {step + 1} / {STEPS.length - 1}
          </div>
        )}

        {/* Skip button */}
        {!isLastStep && (
          <button
            onClick={handleSkip}
            className="absolute right-4 top-4 text-sm text-muted-foreground hover:text-foreground"
          >
            {t("onboarding.skip")}
          </button>
        )}

        <div className="flex flex-col items-center px-6 pb-6 pt-12">
          {/* Illustration */}
          <div className="mb-6 flex h-48 w-full items-center justify-center">
            <Illustration
              name={
                currentStep.illustration as
                  | "travel-plans"
                  | "chat-with-ai"
                  | "share-link"
                  | "celebration"
              }
              alt=""
              className="h-full w-auto max-w-full object-contain"
            />
          </div>

          {/* Title */}
          <h2 className="mb-2 text-center text-xl font-bold">
            {t(`onboarding.${currentStep.translationKey}.title`)}
          </h2>

          {/* Description */}
          <p className="mb-6 text-center text-sm text-muted-foreground">
            {t(`onboarding.${currentStep.translationKey}.description`)}
          </p>

          {/* Tip (only on complete step) */}
          {isLastStep && (
            <div className="mb-6 flex items-start gap-2 rounded-lg bg-primary/5 p-3 text-sm">
              <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span className="text-muted-foreground">{t("onboarding.complete.tip")}</span>
            </div>
          )}

          {/* Progress dots */}
          {!isLastStep && (
            <div className="mb-6 flex justify-center gap-2">
              {STEPS.slice(0, -1).map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    "h-2 w-2 rounded-full transition-colors",
                    index === step ? "bg-primary" : "bg-muted"
                  )}
                />
              ))}
            </div>
          )}

          {/* Navigation button */}
          <Button onClick={handleNext} size="lg" className="w-full">
            {isLastStep ? t("onboarding.start") : t("onboarding.next")}
            {!isLastStep && <ChevronRight className="ml-1 h-4 w-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
